import { useMemo } from 'react';
import type { ProcessingStep } from '@/types/quote';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { CheckCircle } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import { cx } from '@/utils/cx';

interface ProcessingStepsProps {
    steps: ProcessingStep[];
    progress: number;
}

export const ProcessingSteps = ({ steps, progress }: ProcessingStepsProps) => {
    const { t } = useTranslation('pages:quotes');

    const currentStep = useMemo(() => {
        return steps.find(step => step.status === 'processing') || steps.find(step => step.status === 'pending');
    }, [steps]);

    return (
        <div className="mt-4 space-y-3 rounded-lg bg-secondary/50 p-4 border border-secondary">
            {/* Progress Bar */}
            <div className="mb-4">
                <ProgressBar
                    value={progress}
                    min={0}
                    max={100}
                    progressClassName="bg-gradient-to-r from-brand-500 to-brand-600 transition-all duration-500 ease-out"
                />
            </div>

            {/* Steps List */}
            <div className="space-y-2">
                {steps.map((step) => {
                    const isCompleted = step.status === 'completed';
                    const isProcessing = step.status === 'processing';
                    const isPending = step.status === 'pending';

                    return (
                        <div
                            key={step.id}
                            className={cx(
                                "flex items-center gap-3 transition-all duration-300",
                                isCompleted && "opacity-100",
                                isProcessing && "opacity-100",
                                isPending && "opacity-50"
                            )}
                        >
                            {/* Step Icon */}
                            <div className="flex-shrink-0">
                                {isCompleted && (
                                    <CheckCircle className="h-5 w-5 text-success-600" />
                                )}
                                {isProcessing && (
                                    <svg className="h-5 w-5 text-brand-600 animate-spin" viewBox="0 0 20 20" fill="none">
                                        <circle className="opacity-25" cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
                                        <path className="opacity-75" fill="currentColor" d="M4 10a6 6 0 0112 0v1a7 7 0 10-7-7v6z" />
                                    </svg>
                                )}
                                {isPending && (
                                    <div className="h-5 w-5 rounded-full border-2 border-tertiary" />
                                )}
                            </div>

                            {/* Step Label */}
                            <div className="flex-1">
                                <span
                                    className={cx(
                                        "text-sm transition-colors duration-300",
                                        isCompleted && "text-success-600 font-medium",
                                        isProcessing && "text-brand-600 font-semibold",
                                        isPending && "text-tertiary"
                                    )}
                                >
                                    {t(`processing.steps.${step.id}`)}
                                </span>
                            </div>

                            {/* Processing Pulse Effect */}
                            {isProcessing && (
                                <div className="flex-shrink-0">
                                    <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Current Step Highlight */}
            {currentStep && currentStep.status === 'processing' && (
                <div className="mt-3 pt-3 border-t border-secondary">
                    <p className="text-xs text-tertiary">
                        {t('processing.currentStep')}: <span className="text-brand-600 font-medium">{t(`processing.steps.${currentStep.id}`)}</span>
                    </p>
                </div>
            )}
        </div>
    );
};

